import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography,
  FormControl, InputLabel, Select, MenuItem,
  InputAdornment, OutlinedInput, CircularProgress, Autocomplete,
  IconButton
} from '@mui/material';
import axios from 'axios';
import { CurrencySelect } from '../../Recruteur/candidat/AddTalent/CurrencySelect';
import DisponibilitePicker from '../../commun/DisponibilitePicker';
import { FormHelperText } from '@mui/material';
import './Addmission.css';
import DeleteIcon from '@mui/icons-material/Delete';

const countryToCurrency = {
  France: 'EUR',
  Morocco: 'MAD',
  Tunisia: 'TND',
  Egypt: 'EGP',
};

const defaultRemunerationByCountry = {
  France: '',
  Morocco: '',
  Tunisia: '',
  Egypt: '',
};
const StepOne = ({ data, errors, onChange }) => {
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);

  const fetchAutocomplete = async (input) => {
    if (!input || input.length < 2) return;
    setLoadingAutocomplete(true);
    try {
      const res = await axios.get('/api/autocomplete', { params: { input } });
      const predictions = res.data.predictions || [];

      setAutocompleteOptions(predictions.map(p => ({
        label: p.description,
        place_id: p.place_id,
      })));
    } catch (err) {
      console.error("Erreur autocomplete :", err);
    } finally {
      setLoadingAutocomplete(false);
    }
  };

const fetchPlaceDetails = async (placeId) => {
  try {
    const res = await axios.get('/api/place-details', {
      params: { place_id: placeId },
    });
    const components = res.data.result.address_components;
    const country = components.find(c => c.types.includes('country'))?.long_name;
    return country; // ex: "Morocco"
  } catch (err) {
    console.error("Erreur récupération des détails du lieu :", err);
    return null;
  }
};


  return (
    <Box component="form" noValidate className="step-one-form">
      <TextField
        label="Intitulé de la mission"
        required
        value={data.nomMission}
        error={!!errors.nomMission}
        helperText={errors.nomMission}
        onChange={(e) => onChange('nomMission', e.target.value)}
        className="full-width"
      />

      <FormControl error={!!errors.modeTravail} className="half-width">
        <Autocomplete
          freeSolo
          options={autocompleteOptions}
          loading={loadingAutocomplete}
          getOptionLabel={(option) => 
            typeof option === 'string' ? option : option.label
          }
          value={data.lieuxTravail}
          onInputChange={(e, newInput) => {
            onChange('lieuxTravail', newInput);
            onChange('regionTravail', newInput);
            fetchAutocomplete(newInput);
          }}
          onChange={async (e, value) => {
            const description = typeof value === 'string' ? value : value?.label || '';
            const placeId = typeof value === 'string' ? null : value?.place_id;

            onChange('lieuxTravail', description || '');
            onChange('regionTravail', description || '');

            if (placeId) {
              try {
                const res = await axios.get('/api/place-details', {
                  params: { place_id: placeId },
                });

                const components = res.data.result.address_components;
                const detectedCountry = components.find(c => c.types.includes('country'))?.long_name;

                if (detectedCountry) {
                  const currency = countryToCurrency[detectedCountry] || 'EUR';
                  const normalized = detectedCountry.toLowerCase();
                  const isMorocco =
                    normalized.includes('maroc') || normalized.includes('morocco');
                  const defaultRem = isMorocco ? 5000 : 1000;

                  onChange('devise', currency);
                  onChange('remuneration', defaultRem);
                }
              } catch (err) {
                console.error('Erreur lors de la récupération du pays :', err);
              }
            }
          }}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label="Lieu de travail"
              required
              placeholder="Ex : Paris, Casablanca..."
              error={!!errors.lieuxTravail}
              helperText={errors.lieuxTravail}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingAutocomplete && (
                      <CircularProgress color="inherit" size={18} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>

      <FormControl
        required
        error={!!errors.remuneration}
        className="half-width"
        variant="outlined"
      >
        <InputLabel htmlFor="remuneration">Prime</InputLabel>
        <OutlinedInput
          id="remuneration"
          type="number"
          value={data.remuneration}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value)) {
              onChange('remuneration', value);
            }
          }}
          endAdornment={
            <InputAdornment position="end">
              <CurrencySelect
                value={data.devise}
                onChange={(value) => onChange('devise', value)}
              />
            </InputAdornment>
          }
          inputProps={{ min: 0 }}
          label="Prime"
        />
        {errors.remuneration && (
          <Typography variant="caption" color="error">
            {errors.remuneration}
          </Typography>
        )}
        <FormHelperText>
          La prime et la devise sont automatiquement adaptées en fonction du pays sélectionné.
        </FormHelperText>
      </FormControl>

      <FormControl error={!!errors.urgenceMission} className="half-width">
        <InputLabel>Priorité</InputLabel>
        <Select
          value={data.urgenceMission}
          onChange={(e) => onChange('urgenceMission', e.target.value)}
          label="Priorité"
        >
          <MenuItem value="Normale">Normale</MenuItem>
          <MenuItem value="Urgente">Urgente</MenuItem>
        </Select>
      </FormControl>

      <FormControl error={!!errors.modeTravail} className="half-width">
        <InputLabel>Mode</InputLabel>
        <Select
          value={data.modeTravail}
          onChange={(e) => onChange('modeTravail', e.target.value)}
          label="Mode"
        >
          <MenuItem value="Présentiel">Présentiel</MenuItem>
          <MenuItem value="À distance">À distance</MenuItem>
          <MenuItem value="Hybride">Hybride</MenuItem>
        </Select>
      </FormControl>

      <FormControl error={!!errors.dateDebut} className="half-width">
        <DisponibilitePicker
          label="Date de démarrage"
          value={data.dateDebut}
          onChange={(val) => onChange('dateDebut', val)}
          error={!!errors.dateDebut}
          helperText={errors.dateDebut}
          className="DisponibilitePicker"
        />
      </FormControl>


      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} className="half-width">
        <Button
          variant="outlined"
          component="label"
          className="upload-button"
        >
          CHARGER UN DOCUMENT
          <input
            type="file"
            hidden
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  onChange('document', {
                    name: file.name,
                    data: reader.result,
                  });
                };
                reader.readAsDataURL(file);
              }
            }}
          />
        </Button>

        {data.document && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-all',
                flexGrow: 1,
              }}
            >
              {data.document.name}
            </Typography>
            <IconButton
              color="error"
              onClick={() => onChange('document', null)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Box>


      <TextField
        label="Contexte de la mission"
        multiline
        fullWidth
        required
        value={data.contexteMission}
        error={!!errors.contexteMission}
        helperText={errors.contexteMission}
        onChange={(e) => onChange('contexteMission', e.target.value)}
        className="full-width small-textarea"
      />
    </Box>
  );
};

export default StepOne;
